import { Position, SvgData } from '../types';

export class PrinterContext {
  #textState: {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    width: number;
    height: number;
    linespacing: number;
    position: Position;
    codepage: string;
    encoding: 'unicode' | 'codepage';
  };
  #barcodeState: {
    printPosition: 'top' | 'bottom' | 'both' | 'nope';
    hriCharFont: number;
    height: number;
    width: number;
    type:
      | 'upca'
      | 'upce'
      | 'ean13'
      | 'ean8'
      | 'code39'
      | 'interleaved2of5'
      | 'rationalizedCodabar'
      | 'code93'
      | 'code128'
      | 'gs1-128'
      | 'databaromni'
      | 'databartruncated'
      | 'databarlimited'
      | 'databarexpanded'
      | 'code128-auto';
  };
  #pdf417State: {
    colSize: number;
    rowSize: number;
    width: number;
    height: number;
    errorCorrectionLevel: number;
    type: 'standard' | 'truncated';
  };
  #qrcodeState: {
    model: '1' | '2' | 'micro';
    size: number; // n-dots size
    errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H';
  };
  #codeBuffer: string[][];
  #imageBuffer: string;
  #columnImageBuffer: SvgData[];

  constructor() {
    // Initialize and reset all states
    this.resetStates();
  }

  setBarcodePrintPosition(position: 'top' | 'bottom' | 'both' | 'nope') {
    this.#barcodeState.printPosition = position;
  }

  setBarcodeHRICharFont(hriCharFont: number) {
    this.#barcodeState.hriCharFont = hriCharFont;
  }

  setBarcodeHeight(height: number) {
    this.#barcodeState.height = height;
  }

  setBarcodeWidth(width: number) {
    this.#barcodeState.width = width;
  }

  setBarcodeType(
    type:
      | 'upca'
      | 'upce'
      | 'ean13'
      | 'ean8'
      | 'code39'
      | 'interleaved2of5'
      | 'rationalizedCodabar'
      | 'code93'
      | 'code128'
      | 'gs1-128'
      | 'databaromni'
      | 'databartruncated'
      | 'databarlimited'
      | 'databarexpanded'
      | 'code128-auto'
  ) {
    this.#barcodeState.type = type;
  }

  setPdf417ColSize(size: number) {
    this.#pdf417State.colSize = size;
  }

  setPdf417RowSize(size: number) {
    this.#pdf417State.rowSize = size;
  }

  setPdf417Width(width: number) {
    this.#pdf417State.width = width;
  }

  setPdf417Height(height: number) {
    this.#pdf417State.height = height;
  }

  setPdf417ErrorCorrectionLevel(level: number) {
    this.#pdf417State.errorCorrectionLevel = level;
  }

  setPdf417Type(type: 'standard' | 'truncated') {
    this.#pdf417State.type = type;
  }

  setQRCodeModel(model: '1' | '2' | 'micro') {
    this.#qrcodeState.model = model;
  }

  setQRCodeSize(size: number) {
    this.#qrcodeState.size = size;
  }

  setQRCodeErrorCorrectionLevel(level: 'L' | 'M' | 'Q' | 'H') {
    this.#qrcodeState.errorCorrectionLevel = level;
  }

  setBold(isEnable: boolean) {
    this.#textState.bold = isEnable;
  }

  setItalic(isEnable: boolean) {
    this.#textState.italic = isEnable;
  }

  setUnderline(isEnable: boolean) {
    this.#textState.underline = isEnable;
  }

  setWidth(size: number) {
    this.#textState.width = size;
  }

  setHeight(size: number) {
    this.#textState.height = size;
  }

  setLineSpacing(size: number) {
    this.#textState.linespacing = size;
  }

  setPosition(position: Position) {
    this.#textState.position = position;
  }

  setCodepage(codepage: string) {
    this.#textState.codepage = codepage;
  }

  setEncoding(encoding: 'unicode' | 'codepage') {
    this.#textState.encoding = encoding;
  }

  setCodeBuffer(buffer: string[][]) {
    this.#codeBuffer = buffer;
  }

  setImageBuffer(buffer: string) {
    this.#imageBuffer = buffer;
  }

  setColumnImageBuffer(buffer: SvgData[]) {
    this.#columnImageBuffer = buffer;
  }

  getBarcodeState() {
    return this.#barcodeState;
  }

  getPdf417State() {
    return this.#pdf417State;
  }

  getQRCodeState() {
    return this.#qrcodeState;
  }

  getTextState() {
    return this.#textState;
  }

  getCodeBuffer() {
    return this.#codeBuffer;
  }

  getImageBuffer() {
    return this.#imageBuffer;
  }

  getImageColumnBuffer() {
    return this.#columnImageBuffer;
  }

  resetStates() {
    this.#textState = {
      bold: false,
      italic: false,
      underline: false,
      width: 1,
      height: 1,
      linespacing: 54,
      position: 'left',
      codepage: '',
      encoding: 'codepage',
    };

    this.#barcodeState = {
      printPosition: 'nope',
      hriCharFont: 0,
      height: 162,
      width: 3,
      type: 'upca',
    };

    this.#pdf417State = {
      colSize: 0, // automatic processing
      rowSize: 0, // automatic processing
      width: 3,
      height: 3,
      errorCorrectionLevel: 1,
      type: 'standard',
    };

    this.#qrcodeState = {
      model: '2',
      size: 3,
      errorCorrectionLevel: 'L',
    };

    this.#codeBuffer = [];
    this.#imageBuffer = '';
    this.#columnImageBuffer = [];
  }
}
